import { proseMirrorDocShape } from '@techchange/components';
import PropTypes from 'prop-types';

import {
	ENROLLMENT_TYPES,
	MEMBERSHIP_TYPE_ADMIN,
	MEMBERSHIP_TYPE_OWNER,
	MEMBERSHIP_TYPE_STUDENT,
	ORGANIZATION_CATALOG_TYPE_MULTI,
	ORGANIZATION_CATALOG_TYPE_PRIVATE,
	ORGANIZATION_CATALOG_TYPE_SINGLE
} from '../constants/enums';

const ticket = PropTypes.shape({

	/**
	 * The ticket id.
	 */
	id: PropTypes.string.isRequired,

	/**
	 * The name of the ticket.
	 */
	title: PropTypes.string.isRequired,

	/**
	 * True if the ticket is active.
	 */
	active: PropTypes.bool.isRequired,

	/**
	 * The ticket weight.
	 */
	weight: PropTypes.number.isRequired,

	/**
	 * The ticket description.
	 */
	description: PropTypes.string,

	/**
	 * The ticket price. When pay_what_you_want is enabled this is the
	 * suggested/default amount.
	 *
	 * NOTE: money columns are NUMERIC in the database, so the API serializes
	 * them as strings ("35.70") — not numbers.
	 */
	price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,

	/**
	 * True if the buyer chooses the amount they pay for this ticket.
	 */
	pay_what_you_want: PropTypes.bool,

	/**
	 * The minimum amount a buyer may pay when pay_what_you_want is enabled.
	 * Null means no minimum.
	 */
	price_min: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

	/**
	 * The maximum amount a buyer may pay when pay_what_you_want is enabled.
	 * Null means no maximum.
	 */
	price_max: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

	/**
	 * The ticket group (if any).
	 */
	product_ticket_group_id: PropTypes.string,

});

const ticketGroup = PropTypes.shape({

	/**
	 * The ticket group id.
	 */
	id: PropTypes.string.isRequired,

	/**
	 * The name of the ticket group.
	 */
	title: PropTypes.string.isRequired,

	/**
	 * The ticket group description.
	 */
	description: PropTypes.string,

	/**
	 * The ticket group weight.
	 */
	weight: PropTypes.number,

	/**
	 * The associated tickets.
	 */
	tickets: PropTypes.arrayOf(ticket),

});

const product = PropTypes.shape({

	/**
	 * The product id.
	 */
	id: PropTypes.string.isRequired,

	/**
	 * The product title.
	 */
	title: PropTypes.string.isRequired,

	/**
	 * The product type.
	 */
	type: PropTypes.oneOf(['course', 'track']).isRequired,

	/**
	 * The id of the associated course.
	 */
	item_id: PropTypes.string.isRequired,

	/**
	 * The associated item for the product, i.e. course or track.
	 */
	item: PropTypes.shape({

		/**
		 * The badge URL for the product.
		 */
		badge: PropTypes.string,

		/**
		 * The cover image url for the product.
		 */
		cover_image: PropTypes.string,

		/**
		 * The list of courses for the item (only for a track).
		 */
		courses: PropTypes.arrayOf(PropTypes.shape({

			/**
			 * The course ID.
			 */
			id: PropTypes.string.isRequired,

		})),

	}),

	/**
	 * The product price.
	 *
	 * NOTE: NUMERIC in the database, so the API serializes it as a string.
	 */
	price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

	/**
	 * Flag indicating whether or not payment is required.
	 */
	payment: PropTypes.bool.isRequired,

	/**
	 * The ID of the root resource (in Archives) for the
	 * main catalog content of the product.
	 */
	content_main_resource_id: PropTypes.string,

	/**
	 * The ID of the root resource (in Archives) for the
	 * aside catalog content of the product.
	 */
	content_aside_resource_id: PropTypes.string,

	/**
	 * The list of content associated with the product.
	 */
	product_contents: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The content language.
		 */
		language: PropTypes.string.isRequired,

		/**
		 * The product title.
		 */
		title: PropTypes.string.isRequired,

		/**
		 * A custom name that can be used to override
		 * the display label of the associated item.
		 *
		 * i.e. "course" -> "forum", "webinar"
		 */
		type_display_name: PropTypes.string,

		/**
		 * The time commitment expected by a user for the product.
		 */
		time_commitment: PropTypes.string,

		/**
		 * The JSON ProseMirror document for the short description content
		 * to display in the home catalog page for the product.
		 */
		short_description: proseMirrorDocShape,

		/**
		 * The JSON ProseMirror document for the custom message content to display to
		 * users on confirmation of successful registration for the product.
		 */
		confirmation_message: proseMirrorDocShape,

	})),

	/**
	 * The list of product slugs.
	 */
	product_slugs: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The product slug text.
		 */
		slug: PropTypes.string.isRequired,

	})),

	/**
	 * The list of tickets associated with the product.
	 */
	product_tickets: PropTypes.arrayOf(ticket),

	/**
	 * The list of ticket groups associated with the product.
	 */
	product_ticket_groups: PropTypes.arrayOf(ticketGroup),

	/**
	 * The list of associated categories.
	 */
	categories: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The id of the associated category.
		 */
		term_id: PropTypes.string.isRequired,

	})),

});

const course = PropTypes.shape({

	/**
	 * The course ID.
	 */
	id: PropTypes.string.isRequired,

	/**
	 * The course title.
	 */
	title: PropTypes.string.isRequired,

	/**
	 * The course badge, i.e. logo image.
	 */
	badge: PropTypes.string,

	/**
	 * The course cover image.
	 */
	cover_image: PropTypes.string,

});

const track = PropTypes.shape({

	/**
	 * The track ID.
	 */
	id: PropTypes.string.isRequired,

	/**
	 * The track title.
	 */
	title: PropTypes.string.isRequired,

	/**
	 * The track badge, i.e. logo image.
	 */
	badge: PropTypes.string,

	/**
	 * The track cover image.
	 */
	cover_image: PropTypes.string,

	/**
	 * The list of courses for the track.
	 */
	courses: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The course ID.
		 */
		id: PropTypes.string.isRequired,

	})).isRequired,

});

const session = PropTypes.shape({

	/**
	 * The session ID.
	 */
	id: PropTypes.string.isRequired,

	/**
	 * The session slug.
	 */
	slug: PropTypes.string.isRequired,

	/**
	 * True, if session content is open and available to students.
	 */
	is_open: PropTypes.bool.isRequired,

	/**
	 * True, if registration is active for the session.
	 */
	registration_active: PropTypes.bool.isRequired,

	/**
	 * The session start date.
	 */
	start_date: PropTypes.string,

	/**
	 * The session end date.
	 */
	end_date: PropTypes.string,

	/**
	 * The course ID.
	 */
	course_id: PropTypes.string.isRequired,

	/**
	 * The organization ID.
	 */
	organization_id: PropTypes.string.isRequired,

});

const invoiceItem = PropTypes.shape({

	/**
	 * The invoice item ID.
	 */
	id: PropTypes.string.isRequired,

	/**
	 * The original price of the invoice item.
	 *
	 * NOTE: money columns are NUMERIC in the database, so the API serializes
	 * them as strings ("35.70") — not numbers.
	 */
	price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

	/**
	 * The final price of the invoice item.
	 */
	price_quoted: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

	/**
	 * The status of the invoice item.
	 */
	status: PropTypes.string,

	/**
	 * The selected product ticket type (if any) for the invoice item.
	 */
	product_ticket_id: PropTypes.string,

	/**
	 * The product associated with the invoice item.
	 */
	product: product.isRequired,

	/**
	 * The invoice item's selections.
	 */
	selections: PropTypes.shape({

		/**
		 * The invoice item's selected enrollments.
		 */
		enrollments: PropTypes.shape({

			/**
			 * The list of the invoice item's selected session enrollments.
			 */
			sessions: PropTypes.arrayOf(PropTypes.shape({

				/**
				 * The session ID.
				 */
				id: PropTypes.string.isRequired,

				/**
				 * The start date of the session.
				 */
				start_date: PropTypes.string,

				/**
				 * The end date of the session.
				 */
				end_date: PropTypes.string,

			})).isRequired,

		}).isRequired,

	}).isRequired,

});

/**
 * The current organization.
 */
const myOrganization = PropTypes.shape({

	/**
	 * The organization ID.
	 */
	id: PropTypes.string.isRequired,

	/**
	 * The organization's title.
	 */
	title: PropTypes.string.isRequired,

	/**
	 * The organization's slug to create a unique URL.
	 */
	slug: PropTypes.string.isRequired,

	/**
	 * The organization's description, if any.
	 */
	description: PropTypes.string,

	/**
	 * The organization's short title, used where space
	 * is more limited and for the mobile app.
	 * Max length of 12 characters.
	 */
	short_name: PropTypes.string,

	/**
	 * The organization's logo.
	 */
	logo: PropTypes.string,

	/**
	 * The organization's logo link (if any).
	 */
	logo_link: PropTypes.string,

	/**
	 * The organization domain.
	 */
	domain: PropTypes.string.isRequired,

	/**
	 * The organization's default currency.
	 */
	currency: PropTypes.string.isRequired,

	/**
	 * The organization's contact email.
	 */
	contact_email: PropTypes.string,

	/**
	 * The organization's default language.
	 * Defaults to English.
	 */
	language: PropTypes.string.isRequired,

	/**
	 * The type of catalog experience.
	 */
	catalog_type: PropTypes.oneOf([
		ORGANIZATION_CATALOG_TYPE_MULTI,
		ORGANIZATION_CATALOG_TYPE_SINGLE,
		ORGANIZATION_CATALOG_TYPE_PRIVATE,
	]).isRequired,

	/**
	 * The organization's primary color, if any.
	 */
	primary_color: PropTypes.string,

	/**
	 * The organization's secondary color, if any.
	 */
	secondary_color: PropTypes.string,

	/**
	 * The organization's favicon, if any.
	 */
	favicon: PropTypes.string,

	/**
	 * The organization's apple touch icon, if any.
	 */
	apple_touch_icon: PropTypes.string,

	/**
	 * The ID of the selected product for single product catalog types.
	 */
	single_featured_product_id: PropTypes.string,

	/**
	 * True, if payments are enabled.
	 */
	payments_enabled: PropTypes.bool.isRequired,

	/**
	 * True, if video chat is enabled.
	 */
	video_chat_enabled: PropTypes.bool.isRequired,

	/**
	 * Custom label to the Organization's Terms of Service (if one exists)
	 */
	terms_of_use_name: PropTypes.string,

	/**
	 * Link to the Organization's Terms of Service (if one exists)
	 */
	terms_of_use_url: PropTypes.string,

	/**
	 * Custom label for the Organization's Privacy Policy (if one exists)
	 */
	privacy_policy_name: PropTypes.string,

	/**
	 * Link to the Organization's Privacy Policy (if one exists)
	 */
	privacy_policy_url: PropTypes.string,

	/**
	 * The organization data.
	 */
	data: PropTypes.shape({

		/**
		 * The form ID of the post registration form (if any).
		 */
		post_registration_form: PropTypes.string,

		/**
		 * Organization catalog settings.
		 */
		catalog: PropTypes.shape({

			/**
			 * True, if social media banner should be displayed.
			 */
			social_sharing: PropTypes.bool,

			/**
			 * The twitter handle of the organization to use in
			 * the default sharing tweet, used in the social media banner.
			 */
			twitter_handle: PropTypes.string,

			/**
			 * The twitter message to display in the default
			 * sharing tweet, used in the social media banner.
			 */
			twitter_message: PropTypes.string,

			/**
			 * The twitter hashtags to display in the default
			 * sharing tweet, used in the social media banner.
			 */
			twitter_hashtags: PropTypes.string,

			/**
			 * The twitter handles of related accounts to
			 * display alongside the tweet, used in the social media banner.
			 */
			twitter_related: PropTypes.string,

		}),

		/*
		 * The organization's catalog categories for products.
		 */
		categories: PropTypes.arrayOf(PropTypes.shape({

			/**
			 * The category ID.
			 */
			id: PropTypes.string.isRequired,

			/**
			 * The category name.
			 */
			name: PropTypes.string.isRequired,

			/**
			 * The category description.
			 */
			description: PropTypes.string,

			/**
			 * The category slug.
			 */
			slug: PropTypes.string.isRequired,

			/**
			 * The category organization ID.
			 */
			organization_id: PropTypes.string.isRequired,

		})),

		/**
		 * The google analytics settings (if any) for the current organization.
		 */
		googleAnalytics: PropTypes.arrayOf(PropTypes.shape({

			ENABLED: PropTypes.bool,

			TRACKING_ID: PropTypes.string,

			NAME: PropTypes.string

		})),

	}),

});

/**
 * The current user (if successfully logged-in).
 */
const currentUser = PropTypes.shape({

	/**
	 * The user ID.
	 */
	id: PropTypes.string.isRequired,

	/**
	 * The user's display name.
	 */
	display_name: PropTypes.string.isRequired,

	/**
	 * The user email. Optional — not always present on the currentUser object
	 * during initial/SSR render, and no consumer of this shape depends on it
	 * being defined, so requiring it only produced prop-type warnings.
	 */
	email: PropTypes.string,

	/**
	 * The current user's language preference (if any).
	 */
	language: PropTypes.string,

	/**
	 * The list of profile pictures for the user.
	 */
	avatars: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The avatar ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * True, if the image is active.
		 */
		active: PropTypes.bool.isRequired,

		/**
		 * The filstack image url.
		 */
		image: PropTypes.string,

		/**
		 * The filestack security policy.
		 */
		policy: PropTypes.string,

		/**
		 * The filestack security signature.
		 */
		signature: PropTypes.string,

	})),

	/**
	 * The timestamp (if any) when the current user accepted the TechChange terms of service.
	 */
	terms_of_use: PropTypes.string,

	/**
	 * The timestamp (if any) when the current user accepted the TechChange privacy policy.
	 */
	privacy_policy: PropTypes.string,

	/**
	 * The user's memberships.
	 */
	memberships: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The membership id.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * True, if the membership is active.
		 */
		active: PropTypes.bool.isRequired,

		/**
		 * The type of the membership.
		 */
		type: PropTypes.oneOf([
			MEMBERSHIP_TYPE_ADMIN,
			MEMBERSHIP_TYPE_OWNER,
			MEMBERSHIP_TYPE_STUDENT,
		]).isRequired,

		/**
		 * The user's org-wide marketing preference (i.e. ON/OFF).
		 */
		marketing: PropTypes.bool.isRequired,

		/**
		 * The timestamp (if any) when the current user accepted the
		 * Organization's terms of service (if one exists).
		 */
		terms_of_use: PropTypes.string,

		/**
		 * The timestamp (if any) when the current user accepted the
		 * Organization's privacy policy (if one exists).
		 */
		privacy_policy: PropTypes.string,

		/**
		 * The organization ID.
		 */
		organization_id: PropTypes.string.isRequired,

	})).isRequired,

});

/**
 * The Enrollment from Waterfro.
 */
const enrollment = PropTypes.shape({

	/**
	 * The enrollment ID.
	 */
	id: PropTypes.string.isRequired,

	/**
	 * The enrollment type.
	 */
	type: PropTypes.oneOf(
		Object.values(ENROLLMENT_TYPES)
	).isRequired,

	/**
	 * True, if the enrollment is active.
	 */
	active: PropTypes.bool.isRequired,

	/**
	 * The user ID.
	 */
	user_id: PropTypes.string.isRequired,

	/**
	 * The session ID. Used to keep track of the
	 * user's enrollment in a specific course session.
	 */
	session_id: PropTypes.string,

	/**
	 * The course ID. Used to keep track of the
	 * user's enrollment in a specific course session.
	 */
	course_id: PropTypes.string,

	/**
	 * The track ID. Used to keep track of the user's enrollment
	 * in a specific track. The session_id, course_id, and track_enrollment_id
	 * values will be empty.
	 */
	track_id: PropTypes.string,

	/**
	 * The track enrollment ID. Used to keep track of the user's enrollment
	 * in a specific course session for the specific track enrollment. The session_id
	 * and course_id values will be defined, but the track_id will be empty.
	 */
	track_enrollment_id: PropTypes.string,

	/**
	 * The organization ID.
	 */
	organization_id: PropTypes.string.isRequired,

	/**
	 * The associated product information which may optionally be included.
	 */
	product,

});

export default {
	ticket,
	ticketGroup,
	product,
	course,
	track,
	session,
	invoiceItem,
	currentUser,
	myOrganization,
	enrollment,
};
